import { useState, useRef, useEffect } from 'react';
import { X, Lock } from 'lucide-react';

const UNSEEN_IMAGES = [
  { src: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/lmhoy425_Satya_03.png", caption: "Between takes, Bulbbul.", w: "clamp(200px, 26vw, 340px)", aspect: "3/4", locked: false, isNew: false },
  { src: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/r1kcbxoe_DSC_7296.jpeg", caption: "Unscripted.", w: "clamp(170px, 22vw, 280px)", aspect: "4/5", locked: false, isNew: true },
  { src: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/d2k03jag_TUS-8.png", caption: "Rehearsal. Rajasthan. Dawn.", w: "clamp(280px, 38vw, 520px)", aspect: "16/10", locked: false, isNew: false },
  { src: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/esv4cds1_TUS-2.png", caption: "The light before the shot.", w: "clamp(190px, 25vw, 320px)", aspect: "3/4", locked: true, isNew: false },
  { src: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/zurv6zk3_DSC_6239.jpeg", caption: "First read, last page.", w: "clamp(160px, 20vw, 260px)", aspect: "4/5", locked: false, isNew: true },
  { src: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/6sitbrks_ABP_5860.jpg", caption: "Stillness.", w: "clamp(210px, 28vw, 360px)", aspect: "3/4", locked: true, isNew: false },
];

export default function UnseenSection() {
  const [lightbox, setLightbox] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) e.target.querySelectorAll('.reveal-el').forEach(c => c.classList.add('visible')); }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <section id="unseen" data-testid="unseen-section" ref={ref} className="py-24 md:py-40 px-5 md:px-10 lg:px-20">
        <p className="reveal-el font-['Cormorant_Garamond'] text-xs tracking-[0.3em] uppercase mb-4" style={{ color: 'var(--gold)' }}>Unseen</p>
        <h2 className="reveal-el font-['Playfair_Display'] italic text-3xl md:text-4xl font-normal mb-12 md:mb-16" style={{ color: 'var(--warm-cream)' }}>Things only found here.</h2>

        <div className="reveal-el flex items-end gap-5 md:gap-7 overflow-x-auto pb-6 -mx-5 px-5 md:-mx-10 md:px-10 lg:-mx-20 lg:px-20" style={{ scrollbarWidth: 'none' }}>
          {UNSEEN_IMAGES.map((img, i) => (
            <div key={i} className="flex-shrink-0 relative cursor-pointer group" style={{ width: img.w, marginTop: i % 2 === 0 ? '0' : '2rem' }}
              onClick={() => !img.locked && setLightbox(img)}>
              <div className="relative overflow-hidden" style={{ aspectRatio: img.aspect }}>
                <img src={img.src} alt={img.caption} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.02]" loading="lazy" style={img.locked ? { filter: 'blur(8px) brightness(0.5)' } : {}} />
                <div className="absolute inset-2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700" style={{ border: '1px solid rgba(201,168,76,0.2)' }} />
                {img.locked && (
                  <div className="locked-overlay">
                    <Lock className="w-5 h-5" style={{ color: 'var(--gold)' }} strokeWidth={1.5} />
                    <span className="font-['Inter'] text-[9px] tracking-[0.2em] uppercase" style={{ color: 'var(--gold)' }}>Inside Only</span>
                  </div>
                )}
                {img.isNew && !img.locked && (
                  <div className="absolute top-3 right-3 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--gold)' }} />
                    <span className="font-['Inter'] text-[8px] tracking-[0.2em] uppercase" style={{ color: 'var(--gold)' }}>New</span>
                  </div>
                )}
              </div>
              <p className="font-['Inter'] text-[10px] mt-3 tracking-wide opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ color: 'var(--ash)' }}>{img.caption}</p>
            </div>
          ))}
        </div>
      </section>

      {lightbox && (
        <div data-testid="unseen-lightbox" className="fixed inset-0 z-[150] flex items-center justify-center" onClick={() => setLightbox(null)}>
          <div className="absolute inset-0 bg-black/95" />
          <button onClick={() => setLightbox(null)} className="absolute top-5 right-5 z-20 p-3" style={{ color: 'var(--ash)' }}><X className="w-6 h-6" strokeWidth={1} /></button>
          <div className="relative z-10 max-w-4xl max-h-[85vh] px-5" onClick={e => e.stopPropagation()}>
            <img src={lightbox.src} alt={lightbox.caption} className="max-w-full max-h-[80vh] object-contain" />
            <p className="font-['Inter'] text-xs mt-4 text-center" style={{ color: 'var(--ash)' }}>{lightbox.caption}</p>
          </div>
        </div>
      )}
    </>
  );
}
